import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import { PasswordInputComponent } from '../../inputs/password-input/password-input.component';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { AuthService } from '../../../../core/services/auth.service';
import {
  clearToastAction,
  errorToastAction,
  loadingToastAction,
} from '../../../../core/store/actions/toast.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../core/store';
import { GeneralInputComponent } from '../../inputs/general-input/general-input.component';
import { GeneralInputDirective } from '../../../directives/inputs/general-input.directive';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { setStateAction } from '../../../../core/store/actions/invoice.actions';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { ReCaptchaV3Service } from 'ngx-captcha';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-login-ui',
  standalone: true,
  imports: [
    CommonModule,
    SvgIconComponent,
    ReactiveFormsModule,
    PasswordInputComponent,
    RouterModule,
    GeneralInputComponent,
    NzButtonModule,
  ],
  templateUrl: './login-ui.component.html',
  styleUrl: './login-ui.component.css',
})
export class LoginUiComponent {
  //Services
  router = inject(Router);
  route = inject(ActivatedRoute);
  authService = inject(AuthService);
  store = inject(Store<AppState>);
  routeTo = injectQueryParams('routeTo');
  reCaptchaV3Service = inject(ReCaptchaV3Service);

  recaptchaSiteKey = environment.recaptchaSiteKey;

  //Form control
  authForm = new FormGroup({
    email: new FormControl(null, [Validators.required, Validators.email]),
    password: new FormControl(null, [Validators.required]),
    recaptcha: new FormControl<string | null>(null, [Validators.required]),
  });

  private getRoleFromUserPayload(payload: any): string {
    const nestedUser = payload?.user || payload?.data?.user || payload?.profile;
    const roleCandidate =
      payload?.userRole ??
      payload?.role ??
      (payload?.roles || [])?.[0] ??
      nestedUser?.userRole ??
      nestedUser?.role ??
      (nestedUser?.roles || [])?.[0];

    if (typeof roleCandidate === 'string') {
      return roleCandidate.trim();
    }

    if (roleCandidate && typeof roleCandidate === 'object') {
      return (
        roleCandidate?.name ||
        roleCandidate?.role ||
        roleCandidate?.title ||
        ''
      )
        .toString()
        .trim();
    }

    return '';
  }

  private canPersistAuthenticatedUser(role: string): boolean {
    return [
      'governor',
      'superadmin',
      'superadminviewonly',
      'admin',
      'administrator',
      'agent',
      'individual',
      'corporate',
      'state agency',
      'federal agency',
      'expert system',
      'mda user',
      'tax office director',
      'tax office manager',
      'tax officer',
      'support tier1',
      'support tier 1',
      'support',
    ].includes(role);
  }

  // API call (Login)
  mutation = injectMutation(() => ({
    mutationFn: (loginData: { [key: string]: any }) =>
      this.authService.newLogin(loginData),
    onSuccess: (data) => {
      const { id, ...others } = data;
      // Invalidate and refetch by using the client directly

      this.store.dispatch(clearToastAction());
      if (this.routeTo() === 'mda-collections') {
        this.router.navigate([''], {
          queryParams: null,
        });
        this.store.dispatch(setStateAction({ modalState: true }));
        return;
      }
      const role = this.getRoleFromUserPayload(others);
      const normalizedRole = role.toLowerCase();

      if (this.canPersistAuthenticatedUser(normalizedRole)) {
        this.authForm.reset();
        this.authService.setUserData(others);
      }

      // Otherwise, use default role-based navigation
      console.log('🟢 No saved URL, using default navigation for role:', role);
      switch (normalizedRole) {
        case 'governor':
          this.router.navigate(['/governor']);
          break;
        case 'superadmin':
        case 'superadminviewonly': {
          this.router.navigate(['/superadmin']);
          break;
        }
        // this.router.navigate(['/sadmin']);
        // break;
        case 'admin':
        case 'administrator':
          this.router.navigate(['/admin-auth']);
          break;
        case 'agent':
        case 'individual':
        case 'corporate':
        case 'state agency':
        case 'federal agency':
          this.router.navigate(['/user/dashboard']);
          break;
        case 'expert system':
          this.router.navigate(['/tsp']);
          break;
        case 'mda user':
          this.router.navigate(['/MDAmanager']);
          break;
        case 'tax office director':
        case 'tax office manager':
        case 'tax officer': {
          console.log('Redirection triggered!!!==========');
          this.router
            .navigate(['/taxareaoffice-login'])
            .then((success) => {
              console.log('success :>> ', success);
            })
            .catch((e) => {
              console.log('error navigating :>> ', e);
            });
          break;
        }
        case 'support tier1':
        case 'support tier 1':
        case 'support':
          this.router.navigate(['/support-login']);
          break;

        default:
          this.router.navigate(['/']);
          break;
      }
      //OR use the queryClient that is injected into the component
      //this.queryClient.invalidateQueries({ queryKey: ['todos'] })
    },
    onError: (error: any) => {
      this.store.dispatch(
        errorToastAction({
          autohide: true,
          delay: 7000,
          placement: 'top-end',
          message:
            error?.error?.message ||
            'An error occurred while logging in. Try again shortly',
        }),
      );
    },
  }));

  onSubmit() {
    if (
      this.authForm.get('email')?.invalid ||
      this.authForm.get('password')?.invalid
    ) {
      this.authForm.get('email')?.markAsTouched();
      this.authForm.get('password')?.markAsTouched();
      return;
    }

    this.store.dispatch(
      loadingToastAction({
        autohide: false,
        delay: 7000,
        placement: 'top-end',
        message: 'Verifying security check. Please wait...',
      }),
    );

    this.reCaptchaV3Service
      .executeAsPromise(this.recaptchaSiteKey, 'login', {
        useGlobalDomain: false,
      })
      .then((token) => {
        this.authForm.patchValue({ recaptcha: token });
        this.store.dispatch(
          loadingToastAction({
            autohide: false,
            delay: 7000,
            placement: 'top-end',
            message: 'Logging in. Please wait...',
          }),
        );
        this.mutation.mutate(this.authForm.value);
      })
      .catch((error) => {
        console.error('reCAPTCHA error:', error);
        this.store.dispatch(
          errorToastAction({
            autohide: true,
            delay: 7000,
            placement: 'top-end',
            message: 'reCAPTCHA verification failed. Please try again.',
          }),
        );
      });
  }

  goToRegistrationForm(id: string | number) {
    this.router.navigate([`/auth/register/${id}`]);
  }
  switchToRegister() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        a: 'register',
      },
    });
  }
  goBack() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: null,
    });
  }
}
